// Leitner-lite spaced repetition — a *quiet* scheduling utility, not a game.
//
// There are no streaks, no scores, no "you're on fire" counters. The only job
// here is to decide *when* a card should come back. A card lives in a numbered
// box; getting it right moves it up a box (longer interval), getting it wrong
// nudges it down one box (sooner) — gently, never a hard reset to zero.
//
// State is persisted per user_workspace row (srs_box / srs_due_at / srs_reps /
// srs_lapses / last_rating). Pinning an item is the on-ramp: a pinned row with
// no schedule yet is treated as a brand-new card that is due now.

// Days until a card in each box comes back. Box 0 = new / just slipped (today),
// then a widening cadence as the card proves it's known.
export const BOX_INTERVALS_DAYS = [0, 1, 3, 7, 16, 35];
export const MAX_BOX = BOX_INTERVALS_DAYS.length - 1;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Given the previous row state and a rating ("known" | "learning"), compute the
// next schedule fields. Pure — callers persist the returned object.
export function nextSchedule(prev, rating, now = new Date()) {
  const curBox = Number(prev?.srs_box || 0);
  const reps = Number(prev?.srs_reps || 0) + 1;
  let lapses = Number(prev?.srs_lapses || 0);

  let box;
  if (rating === "known") {
    box = Math.min(curBox + 1, MAX_BOX);
  } else {
    box = Math.max(curBox - 1, 0); // step back one box, not all the way to zero
    if (curBox > 0) lapses += 1;
  }

  const due = startOfDay(now);
  due.setDate(due.getDate() + BOX_INTERVALS_DAYS[box]);

  return {
    srs_box: box,
    srs_reps: reps,
    srs_lapses: lapses,
    srs_due_at: due.toISOString(),
    last_rating: rating,
    last_reviewed_at: now.toISOString(),
  };
}

// A scheduled card is due when its due date has arrived (or passed).
export function isScheduled(row) {
  return !!(row && row.srs_due_at);
}

// Reviewable = already scheduled and due, OR pinned but never reviewed (a new
// card waiting to enter the rotation). This makes "Pin" the gentle on-ramp into
// spaced review without a separate "add to review" action.
export function isReviewable(row, now = new Date()) {
  if (!row) return false;
  if (row.srs_due_at) return new Date(row.srs_due_at).getTime() <= now.getTime();
  return !!row.is_pinned;
}

// The working set of rows to review right now.
export function reviewQueue(workspace, now = new Date()) {
  return (workspace || []).filter((w) => isReviewable(w, now));
}

// How many cards are waiting — used by the quiet "Due for review (N)" surface.
export function dueCount(workspace, now = new Date()) {
  return reviewQueue(workspace, now).length;
}

// Next due date across all scheduled rows that are *not* yet due — so an empty
// queue can quietly say "nothing until Thursday" instead of going silent.
export function nextDueDate(workspace, now = new Date()) {
  const upcoming = (workspace || [])
    .filter((w) => w.srs_due_at && new Date(w.srs_due_at).getTime() > now.getTime())
    .map((w) => new Date(w.srs_due_at).getTime());
  return upcoming.length ? new Date(Math.min(...upcoming)) : null;
}
